import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'
import type { Place } from '../shared/contract'
import { US_UNITS, type Units } from '../shared/units'
import { swapToUpdate } from './lib/updateGate'
import { DEMO_PLACES, IS_MOCK } from './mockMode'

export interface SavedPlace extends Place {
  id: string
  /** True for the "My Location" entry. */
  isCurrent?: boolean
  /** My Location from the network's rough position (first visit), not the device's: labelled so. */
  approximate?: boolean
  /** The device fix behind My Location: its stated accuracy (m) and when it was taken (ms epoch). */
  accuracy_m?: number
  fixed_at?: number
  /** How far (and which way) the fix is from the named place: "2 mi NE of Kingston". */
  near?: Near
}

/** Distance and compass bearing (degrees clockwise from north) FROM the named place TO the fix. */
export interface Near {
  distance_km: number
  bearing_deg: number
}

/** What a device fix adds to My Location besides the place itself. */
export interface FixMeta {
  accuracy_m?: number
  fixed_at?: number
  near?: Near | null
}

export type MotionPref = 'auto' | 'reduced'

export function placeId(latitude: number, longitude: number): string {
  return `${latitude.toFixed(3)},${longitude.toFixed(3)}`
}

export function toSaved(p: Place, isCurrent = false): SavedPlace {
  return { ...p, id: isCurrent ? 'current' : placeId(p.latitude, p.longitude), isCurrent: isCurrent || undefined }
}

export const DEFAULT_PLACE: SavedPlace = toSaved({
  name: 'Wilkes-Barre',
  region: 'PA',
  label: 'Wilkes-Barre, PA',
  latitude: 41.2459,
  longitude: -75.8813,
  zip: '18702',
})

interface AppState {
  units: Units
  places: SavedPlace[]
  myLocation: SavedPlace | null
  selectedId: string
  motion: MotionPref
  sidebarOpen: boolean
  /** Keep My Location current while the app is open (Settings → Location). */
  followLocation: boolean
  setUnits: (patch: Partial<Units>) => void
  addPlace: (p: Place) => string
  /** Refine a saved place's naming (e.g. after reverse geocoding a deep link). */
  renamePlace: (id: string, p: Pick<Place, 'name' | 'region' | 'label' | 'zip'>) => void
  removePlace: (id: string) => void
  /** Puts a removed place back where it was (Undo). */
  restorePlace: (p: SavedPlace, index: number) => void
  movePlace: (id: string, toIndex: number) => void
  reorder: (ids: string[]) => void
  /** My Location from the device (precise) — or, with `approximate`, from the network's rough position. */
  setMyLocation: (p: Place | null, approximate?: boolean) => void
  /**
   * A fresh device fix for My Location (precise: clears `approximate`). Never changes what is
   * selected unless `select` — a background update must not pull someone off the place they're reading.
   */
  updateMyLocation: (p: Place, meta: FixMeta, select?: boolean) => void
  setFollowLocation: (on: boolean) => void
  /**
   * First visit: the visitor's approximate area becomes their only place (no one else's
   * default city in the list); "Use precise location" upgrades it in place.
   */
  startWithApproximate: (p: Place) => void
  select: (id: string) => void
  setMotion: (m: MotionPref) => void
  setSidebarOpen: (open: boolean) => void
}

/** localStorage can throw (private mode, blocked storage) — never let that break the app. */
const safeStorage: StateStorage = {
  getItem: (k) => {
    try {
      return localStorage.getItem(k)
    } catch {
      return null
    }
  },
  setItem: (k, v) => {
    try {
      localStorage.setItem(k, v)
    } catch {
      /* storage unavailable: keep in memory only */
    }
  },
  removeItem: (k) => {
    try {
      localStorage.removeItem(k)
    } catch {
      /* ignore */
    }
  },
}

const initialPlaces: SavedPlace[] = IS_MOCK
  ? DEMO_PLACES.map(({ scenario: _s, ...p }) => toSaved(p))
  : [DEFAULT_PLACE]

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      units: US_UNITS,
      places: initialPlaces,
      myLocation: null,
      selectedId: initialPlaces[0].id,
      motion: 'auto',
      sidebarOpen: true,
      followLocation: true,
      setUnits: (patch) => set({ units: { ...get().units, ...patch } }),
      addPlace: (p) => {
        const saved = toSaved(p)
        if (!get().places.some((x) => x.id === saved.id)) set({ places: [...get().places, saved] })
        set({ selectedId: saved.id })
        return saved.id
      },
      renamePlace: (id, p) =>
        set({ places: get().places.map((x) => (x.id === id ? { ...x, name: p.name, region: p.region, label: p.label, zip: p.zip } : x)) }),
      removePlace: (id) => {
        const places = get().places.filter((p) => p.id !== id)
        const my = get().myLocation
        if (!places.length && !my) return // always keep at least one place
        const selectedId =
          get().selectedId === id ? (my?.id ?? places[0]?.id ?? DEFAULT_PLACE.id) : get().selectedId
        set({ places, selectedId })
      },
      restorePlace: (p, index) => {
        if (get().places.some((x) => x.id === p.id)) return
        const places = [...get().places]
        places.splice(Math.max(0, Math.min(places.length, index)), 0, p)
        set({ places })
      },
      movePlace: (id, toIndex) => {
        const places = [...get().places]
        const from = places.findIndex((p) => p.id === id)
        if (from < 0) return
        const [item] = places.splice(from, 1)
        places.splice(Math.max(0, Math.min(places.length, toIndex)), 0, item)
        set({ places })
      },
      reorder: (ids) => {
        const byId = new Map(get().places.map((p) => [p.id, p]))
        const next = ids.map((id) => byId.get(id)).filter((p): p is SavedPlace => !!p)
        if (next.length === get().places.length) set({ places: next })
      },
      setMyLocation: (p, approximate) => {
        if (!p) {
          if (!get().places.length) return // always keep at least one place
          const wasSelected = get().selectedId === 'current'
          set({ myLocation: null, selectedId: wasSelected ? (get().places[0]?.id ?? DEFAULT_PLACE.id) : get().selectedId })
          return
        }
        set({ myLocation: { ...toSaved(p, true), approximate: approximate || undefined }, selectedId: 'current' })
      },
      updateMyLocation: (p, meta, select) => {
        const { near, ...rest } = meta
        set({
          myLocation: { ...toSaved(p, true), ...rest, ...(near ? { near } : {}) },
          ...(select ? { selectedId: 'current' } : {}),
        })
      },
      setFollowLocation: (followLocation) => set({ followLocation }),
      startWithApproximate: (p) => set({ myLocation: { ...toSaved(p, true), approximate: true }, places: [], selectedId: 'current' }),
      select: (id) => {
        const changed = get().selectedId !== id
        set({ selectedId: id })
        // A deploy happened while this tab was open: the place is saved, the new build opens on it.
        if (changed) swapToUpdate()
      },
      setMotion: (motion) => set({ motion }),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    }),
    {
      name: IS_MOCK ? 'wf2-app-demo' : 'wf2-app',
      version: 1,
      storage: createJSONStorage(() => safeStorage),
      partialize: (s) => ({
        units: s.units,
        places: s.places,
        myLocation: s.myLocation,
        selectedId: s.selectedId,
        motion: s.motion,
        sidebarOpen: s.sidebarOpen,
        followLocation: s.followLocation,
      }),
    },
  ),
)

/** All places in display order: My Location first when set. */
export function allPlaces(s: Pick<AppState, 'places' | 'myLocation'>): SavedPlace[] {
  return s.myLocation ? [s.myLocation, ...s.places] : s.places
}

export function selectedPlace(s: Pick<AppState, 'places' | 'myLocation' | 'selectedId'>): SavedPlace {
  const list = allPlaces(s)
  return list.find((p) => p.id === s.selectedId) ?? list[0] ?? DEFAULT_PLACE
}
